<template>
  <b-table
    scrollable
    class="table is-fullwidth auction-table-listing"
    detail-key="rowKey"
    ref="auction-table"
    :data="rows"
    :detailed="displayColumn('Itens')"
    :opened-detailed="
      rows.filter(auction => auction.itens.some(item => item.id)).map(auction => auction.rowKey)
    "
    :show-detail-icon="false"
  >
    <b-table-column
      centered
      field="comprador.nome"
      label="Comprador"
      v-slot="props"
      :sortable="isNotBuyer"
      :visible="displayColumn('Comprador')"
    >
      <div class="auction-column">
        {{ props.row.comprador && props.row.comprador.nome }}
      </div>
    </b-table-column>

    <b-table-column
      centered
      sortable
      field="numeroProcesso"
      label="Processo"
      v-slot="props"
      :visible="displayColumn('Processo')"
    >
      <div class="auction-column">
        {{ props.row.numeroProcesso || 'N/A' }}
      </div>
    </b-table-column>

    <b-table-column
      centered
      sortable
      field="numeroLicitacao"
      label="Número"
      v-slot="props"
      :visible="displayColumn('Número')"
    >
      <div class="auction-column">
        <router-link
          v-if="props.row.situacao && props.row.situacao.id > 1"
          :to="{ name: routeName(props.row), params: { id: props.row.id } }"
        >
          {{ props.row.numeroLicitacao }}
        </router-link>
        <p v-else>{{ props.row.numeroLicitacao || 'N/A' }}</p>
      </div>
    </b-table-column>

    <b-table-column
      centered
      sortable
      field="modalidade"
      label="Modalidade"
      v-slot="props"
      :visible="displayColumn('Modalidade')"
    >
      <div class="auction-column">
        {{ modalidadeLabel(props.row.modalidade || props.row.tipo) }}
      </div>
    </b-table-column>

    <b-table-column
      centered
      sortable
      field="tipoPregao"
      label="Tipo"
      v-slot="props"
      :visible="displayColumn('Tipo')"
    >
      <div class="auction-column">
        {{ formatAuctionType(props.row.tipoPregao) }}
      </div>
    </b-table-column>

    <b-table-column
      centered
      sortable
      field="modoDisputa"
      label="Disputa"
      v-slot="props"
      :visible="displayColumn('Disputa')"
    >
      <div class="auction-column">
        {{ formatDisputeMode(props.row.modoDisputa) }}
      </div>
    </b-table-column>

    <b-table-column
      centered
      sortable
      field="tipoNegociacao.nome"
      label="Negociação"
      v-slot="props"
      :visible="displayColumn('Negociação')"
    >
      <div class="auction-column">
        {{ (props.row.tipoNegociacao && props.row.tipoNegociacao.nome) || 'N/A' }}
      </div>
    </b-table-column>

    <b-table-column
      centered
      sortable
      field="tipoEdital"
      label="Edital"
      v-slot="props"
      :visible="displayColumn('Edital')"
    >
      <div class="auction-column">
        {{ formatNotice(props.row.tipoEdital || props.row.tipoParticipacao) }}
      </div>
    </b-table-column>

    <b-table-column
      centered
      sortable
      field="objeto"
      label="Objeto"
      v-slot="props"
      :visible="displayColumn('Objeto')"
    >
      <div class="auction-column">
        {{ props.row.objeto || 'N/A' }}
      </div>
    </b-table-column>

    <b-table-column
      centered
      sortable
      field="dataPublicacao"
      label="Publicação"
      v-slot="props"
      :visible="displayColumn('Publicação')"
    >
      <div class="auction-column">
        {{ props.row.dataPublicacao ? formatDate(props.row.dataPublicacao) : 'N/A' }}
      </div>
    </b-table-column>

    <b-table-column
      centered
      sortable
      field="dataAbertura"
      label="Abertura"
      v-slot="props"
      :visible="displayColumn('Abertura')"
    >
      <div class="auction-column">
        {{ props.row.dataAbertura ? formatDate(props.row.dataAbertura) : 'N/A' }}
      </div>
    </b-table-column>

    <b-table-column
      centered
      sortable
      field="dataReabertura"
      label="Reabertura"
      v-slot="props"
      :visible="displayColumn('Reabertura')"
    >
      <div class="auction-column">
        {{ props.row.dataReabertura ? formatDate(props.row.dataReabertura) : 'N/A' }}
      </div>
    </b-table-column>

    <b-table-column
      centered
      sortable
      field="situacao.nome"
      label="Situação"
      v-slot="props"
      :visible="displayColumn('Situação')"
    >
      <div class="auction-column">
        {{ situationName(props.row) }}
      </div>
    </b-table-column>

    <template v-slot:detail="props">
      <div v-if="props.row.itens.some(element => element.id)" class="item-container">
        <div
          class="box item-content"
          v-for="item in props.row.itens"
          :key="item.id || item.identificador"
          :ref="'item-container'"
        >
          <div><strong>ID: </strong>{{ item.identificador }}</div>
          <div :class="{ 'elipsis-description': !expandedDescription }" :ref="'description'">
            <strong>Descrição: </strong>{{ item.descricao }}
          </div>
          <div v-if="item.quantidade != null">
            <strong>Quantidade: </strong>{{ item.quantidade }}
          </div>
        </div>
      </div>
    </template>
  </b-table>
</template>

<script>
import { mapGetters } from 'vuex';

import { formatarData as formatDate } from '@/utils/format';

export default {
  name: 'AuctionTable',
  props: {
    items: { type: Array, default: () => [] },
    columns: { type: Array, default: () => [] },
    expandedDescription: { type: Boolean, default: false },
  },
  data() {
    return {
      formatDate,
    };
  },
  computed: {
    ...mapGetters({
      function: 'getFunction',
    }),
    rows() {
      return (this.items || []).map(item => ({
        ...item,
        rowKey: `${item.origem || 'OC'}:${item.id}`,
        itens: Array.isArray(item.itens) ? item.itens : [],
      }));
    },
    isNotBuyer() {
      return (
        ['AUTORIDADE', 'PREGOEIRO', 'AGENTE-CONTRATACAO', 'COMPRADOR'].indexOf(this.function) === -1
      );
    },
  },
  methods: {
    displayColumn(column) {
      return this.columns.some(element => element.name === column && element.display);
    },
    routeName(row) {
      return row.origem === 'DISPENSA_LEGADO' ? 'Dispensa' : 'Pregao';
    },
    modalidadeLabel(modalidade) {
      return (
        {
          PREGAO: 'Pregão Eletrônico',
          CONCORRENCIA: 'Concorrência',
          DISPENSA: 'Dispensa Eletrônica',
        }[modalidade] ||
        modalidade ||
        'N/A'
      );
    },
    formatAuctionType(type) {
      if (!type) return 'N/A';

      return {
        IMEDIATO: 'Contratação Imediata',
        REGISTRO: 'Registro de Preços',
      }[type];
    },
    formatDisputeMode(mode) {
      if (!mode) return 'N/A';

      return {
        ABERTO: 'Aberto',
        FECHADO: 'Fechado',
      }[mode];
    },
    formatNotice(participation) {
      if (!participation) return 'N/A';

      return {
        AMPLO: 'Participação Ampla',
        AMPLOCOTA: 'Com Quota Reservada para ME/EPP/MEI/Cooperativa Preferência',
        RESERVADO: 'Participação Exclusiva de ME/EPP/MEI/Cooperativa Preferência',
      }[participation];
    },
    situationName(row) {
      const situation = (row.situacao && row.situacao.nome) || '';
      if (row.suspensa) return situation ? `${situation} (Suspensa)` : 'Suspensa';
      if (situation === 'Fracassada') return 'Fracassado';
      if (situation === 'Deserta') return 'Deserto';
      if (situation === 'Revogada') return 'Revogado';
      if (situation === 'Encerrada com Vencedor') return 'Encerrado com Vencedor';
      if (situation === 'Encerrada com Recurso') return 'Encerrado com Recurso';
      if (situation === 'Suspensa') return 'Suspenso';
      return situation || 'N/A';
    },
  },
};
</script>

<style lang="scss" scoped>
.auction-table-listing {
  font-size: 0.88rem;
}

.elipsis-description {
  max-width: 50ch;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-container {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 1rem;
}

.item-content {
  background-color: #ededed;
  margin: 1rem 0.5rem;
  min-width: 300px;
  text-align: justify;
}

@media (max-width: 568px) {
  .elipsis-description {
    max-width: 33ch;
  }
}
</style>

<style>
.auction-table-listing > div > table > thead > tr {
  white-space: nowrap;
}

.auction-table-listing > div > table > tbody > tr > td > .detail-container {
  padding: unset !important;
}
</style>
